import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { SITE } from '@/app/constants';
import type { ClawBillingMode, TextPromptWithInputItem } from '@/app/types';
import type { ChatRequestContent } from '../../_hook/use-chat-stream/types';
import {
  buildClawMessageContentsFromInputValue,
  DEFAULT_MEDIA_ROOT,
  isAllowedUploadFile,
  normalizePath,
  toAttachmentContent,
} from '../../_utils';
import type { ChatMessageItem, ClawSendFile, SessionMode, UploadClawFileResult, YoloMessageOptions } from '../../types';
import { isMarkdownFile } from '../File/utils';
import type { PendingUploadAttachment } from './ChatInput';
import { buildYoloSessionMediaFolderPath } from './chat-scene-paths';

interface PendingUploadMeta {
  order: number;
  tempFilePath: string;
  targetFolderPath: string;
}

interface UploadedPendingFile extends ClawSendFile {
  order: number;
  tempFilePath: string;
}

interface PendingDraftSnapshot {
  inputValue?: TextPromptWithInputItem;
  text?: string;
  files: ClawSendFile[];
}

interface HandleSendArgs {
  content: string;
  inputValue?: TextPromptWithInputItem;
  files: ClawSendFile[];
  pendingFiles?: File[];
  pendingAttachments?: PendingUploadAttachment[];
  quickPromptLabel?: string;
  sessionMode?: SessionMode;
  yoloOptions?: YoloMessageOptions;
  billingMode?: ClawBillingMode;
}

interface UploadOutcome<TData> {
  ok: boolean;
  data?: TData;
  clawInactive?: boolean;
  conflict?: boolean;
  moderateRejected?: boolean;
}

interface UploadWithRenameOnConflictArgs<TData, TMeta> {
  fileEntries: Array<{ file: File; meta: TMeta }>;
  targetFolderPath: string;
  isAllowedFile: (file: File) => boolean;
  uploadOnce: (file: File) => Promise<UploadOutcome<TData>>;
  onSuccess: (uploadedFile: File, data?: TData, meta?: TMeta) => void;
}

type UploadWithRenameOnConflict = <TData, TMeta>(
  options: UploadWithRenameOnConflictArgs<TData, TMeta>,
) => Promise<unknown>;

interface ClawErrorLike {
  reason?: string;
  fileName?: string;
}

interface CreateSessionResult {
  data?: {
    session_id?: string;
  } | null;
  error?: ClawErrorLike | null;
}

interface UploadClawFileResponse {
  data?: UploadClawFileResult | null;
  error?: ClawErrorLike | null;
}

interface CreateChatSceneSendHandlerOptions {
  activeSessionId: string | null;
  /**
   * 实时读取当前 claw id（而非创建 handler 时闭包捕获）。
   * 未开通态点发送会先 await 开通，开通成功后 store 才写入 activeClaw；
   * 此时本 handler 仍是开通前创建的旧闭包，必须实时读 store 才能拿到新 claw id，
   * 否则建 session 时 clawId 仍为 undefined 直接失败。
   */
  getActiveClawId: () => string | undefined;
  clawFolderPath: string;
  sessionMediaFolderMap: Record<string, string>;
  createSessionAsync: (options: { clawId: string; name: string; mode?: SessionMode }) => Promise<CreateSessionResult>;
  handleClawInactiveError: (error?: ClawErrorLike | null) => boolean;
  prependSession: (session: {
    id: string;
    name: string;
    created_at: string;
    updated_at: string;
    mode?: SessionMode;
  }) => void;
  replaceClawUrlWithoutNavigation: (sessionId?: string) => void;
  markAsNewSession: (sessionId: string) => void;
  newlyCreatedSessionIdsRef: MutableRefObject<Set<string>>;
  setActiveSessionId: (sessionId: string | null) => void;
  createClawFolder: (options: { name: string; parentPath: string }) => Promise<{ error?: ClawErrorLike | null }>;
  setClawFolderPath: (path: string) => void;
  setSessionMediaFolder: (sessionId: string, folderPath: string) => void;
  bumpClawFileListRefreshKey: () => void;
  uploadClawFile: (options: { clawId: string; file: File; folderPath: string }) => Promise<UploadClawFileResponse>;
  uploadWithRenameOnConflict: UploadWithRenameOnConflict;
  setMessages: Dispatch<SetStateAction<ChatMessageItem[]>>;
  sendDraftSnapshotRef: MutableRefObject<Map<string, PendingDraftSnapshot>>;
  startChatStream: (options: {
    assistantMessageId: string;
    payload: {
      session_id: string;
      model_version?: string;
      resolution?: string;
      billing_mode?: ClawBillingMode;
      msg: {
        role: 'user';
        contents: ChatRequestContent[];
      };
    };
  }) => void;
  shouldAutoScrollRef: MutableRefObject<boolean>;
  /**
   * 乐观新建的 session 没能发出首条消息时（内容审核未通过 / 暂存附件上传失败等）触发。
   * 上层据此结束"等待首条消息"的 loading 态，让页面停在新建 session 的空态而非永久 loading，
   * 输入框草稿保持不变，用户可改后重发。
   */
  onFirstSendAfterCreateAborted?: (sessionId: string) => void;
}

interface ResolvePendingUploadTargetFolderPathOptions {
  attachment: Pick<PendingUploadAttachment, 'targetFolderPath'> | { targetFolderPath?: string };
  sessionMode?: SessionMode;
  sessionFolderPath: string;
  clawFolderPath: string;
}

export const resolvePendingUploadTargetFolderPath = ({
  attachment,
  sessionMode,
  sessionFolderPath,
  clawFolderPath,
}: ResolvePendingUploadTargetFolderPathOptions) => {
  if (sessionMode === 'yolo') return sessionFolderPath || DEFAULT_MEDIA_ROOT;

  const targetFolderPath = normalizePath(attachment.targetFolderPath);
  if (targetFolderPath === DEFAULT_MEDIA_ROOT || targetFolderPath.startsWith(`${DEFAULT_MEDIA_ROOT}/`)) {
    return targetFolderPath;
  }

  const currentMediaFolderPath = normalizePath(clawFolderPath);
  if (currentMediaFolderPath === DEFAULT_MEDIA_ROOT || currentMediaFolderPath.startsWith(`${DEFAULT_MEDIA_ROOT}/`)) {
    return currentMediaFolderPath;
  }

  return DEFAULT_MEDIA_ROOT;
};

export const createChatSceneSendHandler = ({
  activeSessionId,
  getActiveClawId,
  clawFolderPath,
  sessionMediaFolderMap,
  createSessionAsync,
  handleClawInactiveError,
  prependSession,
  replaceClawUrlWithoutNavigation,
  markAsNewSession,
  newlyCreatedSessionIdsRef,
  setActiveSessionId,
  createClawFolder,
  setClawFolderPath,
  setSessionMediaFolder,
  bumpClawFileListRefreshKey,
  uploadClawFile,
  uploadWithRenameOnConflict,
  setMessages,
  sendDraftSnapshotRef,
  startChatStream,
  shouldAutoScrollRef,
  onFirstSendAfterCreateAborted,
}: CreateChatSceneSendHandlerOptions) => {
  return async ({
    content,
    inputValue,
    files,
    pendingFiles,
    pendingAttachments,
    quickPromptLabel,
    sessionMode,
    yoloOptions,
    billingMode,
  }: HandleSendArgs): Promise<boolean> => {
    // 实时读取 claw id：未开通态发送会先 await 开通，开通后才写入 store，
    // 旧闭包必须在此刻读取才能拿到新建的 claw。
    const activeClawId = getActiveClawId();
    let targetSessionId = activeSessionId;
    const trimmedQuickLabel = quickPromptLabel?.trim();
    const pendingUploadAttachments: PendingUploadAttachment[] = pendingAttachments?.length
      ? pendingAttachments
      : (pendingFiles ?? []).map((file) => ({ file, tempFilePath: '' }));
    const sessionName = trimmedQuickLabel ? trimmedQuickLabel : SITE === 'china' ? '新对话' : 'New Chat';
    const initialMessageContentCount = (content.trim() ? 1 : 0) + files.length + pendingUploadAttachments.length;
    if (!initialMessageContentCount) {
      return false;
    }

    shouldAutoScrollRef.current = true;

    let sessionFolderPath = activeSessionId ? sessionMediaFolderMap[activeSessionId] : '';
    let createdSessionId: string | null = null;

    // 本次乐观新建的 session 没能发出首条消息时（典型：暂存附件内容审核未通过 / 上传失败）的收尾。
    // 不回滚到欢迎态、保留这个新建 session（用户停在新 session 页，输入框草稿原样保留可改后重发），
    // 但必须通知上层"首条消息已结束（失败）"，否则 deriveIsAwaitingFirstMessageAfterCreate
    // 会一直为 true（URL 只走了 replaceState、Next route param 仍为空、messages 也为空），页面永久卡 loading。
    const abortCreatedSessionFirstSend = (sessionId: string) => {
      onFirstSendAfterCreateAborted?.(sessionId);
    };

    if (!targetSessionId) {
      if (!activeClawId) return false;
      const createSessionResult = await createSessionAsync({
        clawId: activeClawId,
        name: sessionName,
        mode: sessionMode,
      });
      if (createSessionResult.error || !createSessionResult.data?.session_id) {
        handleClawInactiveError(createSessionResult.error);
        return false;
      }

      targetSessionId = createSessionResult.data.session_id;
      createdSessionId = targetSessionId;
      const optimisticSessionTime = new Date().toISOString();
      prependSession({
        id: targetSessionId,
        name: sessionName,
        created_at: optimisticSessionTime,
        updated_at: optimisticSessionTime,
        ...(sessionMode ? { mode: sessionMode } : {}),
      });
      replaceClawUrlWithoutNavigation(targetSessionId);
      markAsNewSession(targetSessionId);
      newlyCreatedSessionIdsRef.current.add(targetSessionId);
      setActiveSessionId(targetSessionId);

      if (activeClawId && sessionMode === 'yolo') {
        const folderName = `sess-${targetSessionId}`;
        const folderPath = buildYoloSessionMediaFolderPath(targetSessionId);
        const { error: folderError } = await createClawFolder({
          name: folderName,
          parentPath: DEFAULT_MEDIA_ROOT,
        });
        const handledInactive = folderError ? handleClawInactiveError(folderError) : false;
        if (handledInactive) {
          setClawFolderPath(DEFAULT_MEDIA_ROOT);
        } else {
          setSessionMediaFolder(targetSessionId, folderPath);
          setClawFolderPath(folderPath);
          bumpClawFileListRefreshKey();
          sessionFolderPath = folderPath;
        }
      }
    }

    const uploadedPendingFiles: UploadedPendingFile[] = [];
    if (pendingUploadAttachments.length && targetSessionId && activeClawId) {
      const entriesByFolderPath = new Map<string, Array<{ file: File; meta: PendingUploadMeta }>>();
      pendingUploadAttachments.forEach((attachment, order) => {
        const targetFolderPath = resolvePendingUploadTargetFolderPath({
          attachment,
          sessionMode,
          sessionFolderPath,
          clawFolderPath,
        });
        const entries = entriesByFolderPath.get(targetFolderPath) ?? [];
        entries.push({
          file: attachment.file,
          meta: {
            order,
            tempFilePath: attachment.tempFilePath,
            targetFolderPath,
          },
        });
        entriesByFolderPath.set(targetFolderPath, entries);
      });

      for (const [targetFolderPath, fileEntries] of entriesByFolderPath) {
        // eslint-disable-next-line no-await-in-loop
        await uploadWithRenameOnConflict<UploadClawFileResult, PendingUploadMeta>({
          fileEntries,
          targetFolderPath,
          isAllowedFile: isAllowedUploadFile,
          uploadOnce: async (file) => {
            const { data, error } = await uploadClawFile({
              clawId: activeClawId,
              file,
              folderPath: targetFolderPath,
            });
            if (data) return { ok: true, data };
            return {
              ok: false,
              clawInactive: error?.reason === 'claw_is_inactive',
              conflict: error?.reason === 'Conflict',
              moderateRejected: error?.reason === 'moderate_rejected',
            };
          },
          onSuccess: (_uploadedFile, data, meta) => {
            if (!data || !meta) return;
            uploadedPendingFiles.push({
              file_path: data.file_path,
              file_type: data.file_type,
              order: meta.order,
              tempFilePath: meta.tempFilePath,
            });
          },
        });
      }
    }

    uploadedPendingFiles.sort((prev, next) => prev.order - next.order);
    if (pendingUploadAttachments.length && uploadedPendingFiles.length !== pendingUploadAttachments.length) {
      // 暂存附件没有全部上传成功（含内容审核未通过）：本轮无内容可发。
      // 保留这个乐观新建的 session，但要结束首条消息的等待态，避免新 session 页永久卡 loading。
      if (createdSessionId) {
        abortCreatedSessionFirstSend(createdSessionId);
      }
      return false;
    }
    const pendingAttachmentReplacements = uploadedPendingFiles.map((file) => ({
      tempFilePath: file.tempFilePath,
      filePath: file.file_path,
    }));
    const { text: contentForSend, skillContents } = inputValue
      ? buildClawMessageContentsFromInputValue(inputValue, pendingAttachmentReplacements)
      : { text: content.trim(), skillContents: [] as ChatRequestContent[] };
    const allFiles = [...files, ...uploadedPendingFiles];
    if (uploadedPendingFiles.length) {
      bumpClawFileListRefreshKey();
    }

    const messageContents: ChatRequestContent[] = [
      ...(contentForSend
        ? [
            {
              type: 'text' as const,
              content: contentForSend,
            },
          ]
        : []),
      ...allFiles.map((file) => ({
        type: isMarkdownFile(file.file_path) ? ('markdown' as const) : file.file_type,
        content: file.file_path,
      })),
      ...skillContents,
    ];

    if (!messageContents.length) {
      if (createdSessionId) {
        abortCreatedSessionFirstSend(createdSessionId);
      }
      return false;
    }

    const assistantMessageId = `assistant-${Date.now()}`;
    setMessages((prev) => {
      return [
        ...prev,
        {
          id: `user-${Date.now()}`,
          role: 'user',
          content:
            contentForSend ||
            allFiles
              .map((file) => `[${file.file_type}] ${file.file_path}`)
              .join('\n')
              .trim(),
          fileList: allFiles.map((file) =>
            isMarkdownFile(file.file_path)
              ? { content: file.file_path, type: 'markdown' as const }
              : toAttachmentContent(file),
          ),
          createdAt: new Date().toISOString(),
        },
        {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          thinkingLoading: true,
        },
      ];
    });

    sendDraftSnapshotRef.current.set(assistantMessageId, {
      inputValue,
      text: contentForSend,
      files: allFiles,
    });

    startChatStream({
      assistantMessageId,
      payload: {
        session_id: targetSessionId,
        ...(sessionMode === 'yolo' && yoloOptions
          ? {
              model_version: yoloOptions.modelVersion,
              resolution: yoloOptions.resolution,
            }
          : {}),
        ...(billingMode ? { billing_mode: billingMode } : {}),
        msg: {
          role: 'user',
          contents: messageContents,
        },
      },
    });

    return true;
  };
};
